var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext } from "react";
import { FilteredOverviewPage } from "../../components/FilteredOverviewPage/FilteredOverviewPage";
import { CohortFilterSet } from "../../components/Filters/Filters";
import { DownloadableSection } from "../../components/DownloadableSection/DownloadableSection";
import { mapCohortCategory, useGetCohortReportLeaderCount, useGetLeaderScoresAssessmentAggByFactorQuery, useGetRestrictedCohortReport, useGetSingleAvailableCohortReportsQuery, } from "../../store/api/core";
import { FilterContext, } from "../../providers/FilterProvider";
import { useConfidentialReportView } from "../../hooks/confidentialReportView";
import styles from "./CohortInsightsReportPages.module.scss";
import FactorChart from "../../components/FactorChart";
import { useParams } from "react-router-dom";
import { CohortInsightsBubbles, SelectableSkillCohortInsightsBubbles, } from "../../components/CohortInsights/CohortInsights";
import { AverageBarConfigMap, Colors, SwiftSkill, Tab, } from "../../common/cohortHelpers";
import { labelMap } from "../../common/cohortConfiguration";
import { LevelledFramework } from "../../components/LevelledFramework/LevelledFramework";
import { CBILegendContent, hoganHDSLegendContent, hoganHPILegendContent, hoganMVPILegendContent, ImmersiveAgilityLegendContent, ImmersiveMetaLegendContent, swiftLegendContent, waveLegendContent, } from "./legendContentData";
import SwiftFactorChart from "../../components/SwiftFactorChart";
import { cohortSet } from "../../common/cohortConfiguration";
import LeaderCount from "../../components/LeaderCount";
import { useRestrictionSetup } from "../../hooks/restrictionSetup";
import SpinnerAndErrorer from "../../components/SpinnerAndErrorer";
import { AvailableFilters, getEntity } from "../../common/filters";
import { HoganFactorChart } from "../../components/HoganFactorChart/HoganFactorChart";
import { MutateReportButtons } from "../../components/Cohort/cohort";
import ImmersiveAgilityFactorChart from "../../components/ImmersiveAgilityFactorChart";
import { Breadcrumb } from "../../components/Breadcrumb/Breadcrumb";
var BubbleSubHeader = function (_a) {
    var title = _a.title, color = _a.color;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.levelCircle, style: { backgroundColor: color } }), _jsx("div", __assign({ className: styles.levelText }, { children: title || "Leaders" })), _jsx("div", { className: styles.verticalDivider })] }));
};
var BUBBLE_SUBHEADER_COLOR = "var(--grey-color-60)";
var TabContent = function (_a) {
    var legendContent = _a.legendContent, factorChart = _a.factorChart, bubbles = _a.bubbles;
    return (_jsxs("div", __assign({ className: styles.tabContent }, { children: [_jsx("div", { className: styles.right }), _jsx("div", { className: styles.left }), _jsx(LevelledFramework, { legendContent: legendContent }), _jsx("div", __assign({ className: styles.factors }, { children: _jsx(DownloadableSection, __assign({ filename: "factors.png" }, { children: factorChart })) })), bubbles && (_jsx("div", __assign({ className: styles.bubbles }, { children: _jsx(DownloadableSection, __assign({ filename: "cohort-insight-bubbles.png", buttonStyles: styles.bubbleChartButton }, { children: _jsxs("div", __assign({ className: styles.leaderDistChartContainer }, { children: [_jsx("div", __assign({ className: styles.leaderDistChartFloatContainer }, { children: _jsxs("div", __assign({ className: styles.leaderDistChartHeaderContainer }, { children: [_jsx("div", __assign({ className: styles.bubblesTitle }, { children: bubbles.title })), _jsx("div", __assign({ className: styles.leaderDistChartHeaderSub }, { children: bubbles.subHeader }))] })) })), _jsx("div", __assign({ className: styles.leaderDistChartStyle }, { children: bubbles.chart }))] })) })) })))] })));
};
export var TabbedCohortInsightsPage = function (_a) {
    var _b, _c;
    var restrictionId = _a.restrictionId;
    var restrictions = useGetRestrictedCohortReport({ reportId: restrictionId }, { shouldSkipCall: function () { return !restrictionId; } });
    useRestrictionSetup(((_c = (_b = restrictions.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.filters) || {});
    return _jsx(CohortInsightsReportPages, {});
};
export var CohortInsightsReportPages = function () {
    var _a, _b, _c, _d, _e, _f;
    var _g = useContext(FilterContext), getFilters = _g.getFilters, updateFilters = _g.updateFilters;
    var isProjectSingleSelect = ((_b = (_a = getFilters()) === null || _a === void 0 ? void 0 : _a.project) === null || _b === void 0 ? void 0 : _b.length) == 1 ? true : false;
    var reportId = useParams().reportId;
    var reportData = useGetSingleAvailableCohortReportsQuery({
        reportId: reportId || "",
    }, { shouldSkipCall: function () { return !reportId; } });
    var _h = reportData.data || {}, reportName = _h.reportName, assessmentCategory = _h.assessmentCategory, isConfidential = _h.isConfidential;
    var disableFilters = useConfidentialReportView(isConfidential).disableFilters;
    var mappedCategory = mapCohortCategory(assessmentCategory);
    // Set assessmentCategory in filters when it loads
    React.useEffect(function () {
        var _a;
        if (!mappedCategory) {
            return;
        }
        updateFilters((_a = {},
            _a[getEntity(AvailableFilters.AssessmentCategory)] = [mappedCategory],
            _a));
        document.title = "Cohort Insights";
    }, [mappedCategory]);
    function isAverageBarConfigKey(key) {
        return key in AverageBarConfigMap;
    }
    var configKey = isAverageBarConfigKey(assessmentCategory || "")
        ? assessmentCategory
        : Tab.CBI;
    var factorScoreData = useGetLeaderScoresAssessmentAggByFactorQuery({
        category: assessmentCategory || "",
        filters: getFilters(),
        reportId: reportId || "",
    }, { shouldSkipCall: function () { return !reportId; } });
    var _j = ((_c = factorScoreData === null || factorScoreData === void 0 ? void 0 : factorScoreData.data) === null || _c === void 0 ? void 0 : _c.data) || {}, Capabilities = _j.Capabilities, Potential = _j.Potential, capabilities_skillgroup_details = _j.capabilities_skillgroup_details, potential_skillgroup_details = _j.potential_skillgroup_details, overall_skillgroup_details = _j.overall_skillgroup_details, verbal_reasoning_skillgroup_details = _j.verbal_reasoning_skillgroup_details, numerical_reasoning_skillgroup_details = _j.numerical_reasoning_skillgroup_details, abstract_reasoning_skillgroup_details = _j.abstract_reasoning_skillgroup_details, hogan_hpi = _j.hogan_hpi, hogan_hds = _j.hogan_hds, hogan_mvpi = _j.hogan_mvpi, foresight_skillgroup_details = _j.foresight_skillgroup_details, learning_skillgroup_details = _j.learning_skillgroup_details, adaptability_skillgroup_details = _j.adaptability_skillgroup_details, resilience_skillgroup_details = _j.resilience_skillgroup_details, foresight = _j.foresight, learning = _j.learning, adaptability = _j.adaptability, resilience = _j.resilience;
    var mapToSkillGroupData = function (data) {
        var _a, _b, _c, _d;
        return ({
            average: (_a = data === null || data === void 0 ? void 0 : data.average) !== null && _a !== void 0 ? _a : 0,
            belowTargetCount: (_b = data === null || data === void 0 ? void 0 : data.belowTargetCount) !== null && _b !== void 0 ? _b : 0,
            buckets: (_c = data === null || data === void 0 ? void 0 : data.buckets) !== null && _c !== void 0 ? _c : [],
            skillGroupName: (_d = data === null || data === void 0 ? void 0 : data.skillGroupName) !== null && _d !== void 0 ? _d : "",
        });
    };
    var factorChartData = {
        title: "Scoring by META Factor",
        colors: Colors[configKey],
        start: AverageBarConfigMap[configKey].start,
        end: AverageBarConfigMap[configKey].end,
        scale: AverageBarConfigMap[configKey].scale,
        overallCapabilitites: mapToSkillGroupData(capabilities_skillgroup_details),
        overallPotential: mapToSkillGroupData(potential_skillgroup_details),
        capabilititesFactorData: Capabilities,
        potentialFactorData: Potential,
        averageScoreLabel: (_d = labelMap.averageScoreLabel[assessmentCategory]) !== null && _d !== void 0 ? _d : "Average Score",
        distributionPercentageLabel: (_e = labelMap.distributionPercentageLabel[assessmentCategory]) !== null && _e !== void 0 ? _e : "Percent Score Distribution",
        isProjectSingleSelect: isProjectSingleSelect,
        category: assessmentCategory,
        pageType: assessmentCategory == Tab.CBI ? true : false,
        reportId: reportId || "",
        headerRoute: "CohortCategorizedSkillRestricted",
        skillRoute: "CohortInsightsSkillRestricted",
    };
    var swiftSkillGroupDetails = [
        overall_skillgroup_details,
        verbal_reasoning_skillgroup_details,
        numerical_reasoning_skillgroup_details,
        abstract_reasoning_skillgroup_details,
    ];
    var swiftFactorChartData = {
        title: "Average Sten Scores and Distribution",
        colors: Colors[configKey],
        start: AverageBarConfigMap[configKey].start,
        end: AverageBarConfigMap[configKey].end,
        scale: AverageBarConfigMap[configKey].scale,
        swiftSkillGroupDetails: swiftSkillGroupDetails,
        averageScoreLabel: "Average Sten Score",
        distributionPercentageLabel: "Sten Score Distribution",
        isProjectSingleSelect: isProjectSingleSelect,
        category: Tab.SavilleSwiftAptitude,
        pageType: false,
        reportId: reportId || "",
        headerRoute: "CohortCategorizedSkillRestricted",
    };
    var immersiveAgilityFactorChartData = {
        title: "Scoring by Agility Drive Factors & Competencies",
        colors: Colors[Tab.ImmersiveAgility],
        start: AverageBarConfigMap[configKey].start,
        end: AverageBarConfigMap[configKey].end,
        scale: AverageBarConfigMap[configKey].scale,
        overallForesight: foresight_skillgroup_details,
        overallLearning: learning_skillgroup_details,
        overallAdaptability: adaptability_skillgroup_details,
        overallResilience: resilience_skillgroup_details,
        foresightFactorData: foresight,
        learningFactorData: learning,
        adaptabilityFactorData: adaptability,
        resilienceFactorData: resilience,
        averageScoreLabel: "Average Score",
        distributionPercentageLabel: "Percent Score Distribution",
        isProjectSingleSelect: false,
        category: Tab.ImmersiveAgility,
        reportId: reportId || "",
        headerRoute: "CohortCategorizedSkillRestricted",
        skillRoute: "CohortInsightsSkillRestricted",
    };
    var hoganHDSFactorChart = {
        title: "Average Percentile Scores by HDS Scale",
        colors: Colors[Tab.HoganHDS],
        reportId: reportId || "",
        steps: AverageBarConfigMap[configKey].scale,
        hoganSkillsDetails: hogan_hds,
        averagePercentileLabel: "Average Percentile Score",
        distributionPercentageLabel: "Percentile Score Distribution",
        headerRoute: "CohortCategorizedSkillRestricted",
    };
    var hoganHPIFactorChart = {
        title: "Average Percentile Scores by HPI Scale",
        colors: Colors[Tab.HoganHPI],
        reportId: reportId || "",
        steps: AverageBarConfigMap[configKey].scale,
        hoganSkillsDetails: hogan_hpi,
        averagePercentileLabel: "Average Percentile Score",
        distributionPercentageLabel: "Percentile Score Distribution",
        headerRoute: "CohortCategorizedSkillRestricted",
    };
    var hoganMVPIFactorChart = {
        title: "Average Percentile Scores by MVPI Scale",
        colors: Colors[Tab.HoganMVPI],
        reportId: reportId || "",
        steps: AverageBarConfigMap[configKey].scale,
        hoganSkillsDetails: hogan_mvpi,
        averagePercentileLabel: "Average Percentile Score",
        distributionPercentageLabel: "Percentile Score Distribution",
        headerRoute: "CohortCategorizedSkillRestricted",
    };
    var leaderCountData = useGetCohortReportLeaderCount({
        category: assessmentCategory,
        filters: getFilters(),
    }, { shouldSkipCall: function () { return !reportId; } }).data;
    // Wait for assessmentCategory to load AND be set in filters before rendering
    if (!assessmentCategory) {
        return _jsx(SpinnerAndErrorer, { conditions: [reportData, factorScoreData] });
    }
    // Render dynamic content based on current category
    var renderContent = function () {
        var _a;
        var contentConfig = (_a = {},
            _a[Tab.ImmersiveAgility] = {
                legendContent: ImmersiveAgilityLegendContent,
                factorChart: (_jsx(ImmersiveAgilityFactorChart, { data: immersiveAgilityFactorChartData })),
            },
            _a[Tab.SavilleSwiftAptitude] = {
                legendContent: swiftLegendContent,
                factorChart: _jsx(SwiftFactorChart, { data: swiftFactorChartData }),
                bubbles: {
                    title: "Aptitude vs. Pace Leader Distribution for:",
                    subHeader: _jsx(BubbleSubHeader, { color: BUBBLE_SUBHEADER_COLOR }),
                    chart: (_jsx(SelectableSkillCohortInsightsBubbles, { category: Tab.SavilleSwiftAptitude, reportId: reportId || "", colorDescriptions: Colors[Tab.SavilleSwiftAptitude], availableLabels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], bubbleScoreRounder: function (value) { return (value === null || value === void 0 ? void 0 : value.toFixed(1)) || ""; }, keyedLabels: cohortSet[Tab.SavilleSwiftAptitude][SwiftSkill.Abstract]
                            .bubbleChartFields, gridPositions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] })),
                },
            },
            _a[Tab.SavilleWave] = {
                legendContent: waveLegendContent,
                factorChart: _jsx(FactorChart, { data: factorChartData }),
                bubbles: {
                    title: "Agility & Potential vs. Leadership Capabilities Leader Distribution",
                    subHeader: _jsx(BubbleSubHeader, { color: BUBBLE_SUBHEADER_COLOR }),
                    chart: (_jsx(CohortInsightsBubbles, { category: Tab.SavilleWave, reportId: reportId || "", colorDescriptions: Colors[Tab.SavilleWave], availableLabels: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], bubbleScoreRounder: function (value) { return (value === null || value === void 0 ? void 0 : value.toFixed(1)) || ""; }, keyedLabels: cohortSet[Tab.SavilleWave]["Potential"].bubbleChartFields, gridPositions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], showAxisBarBorders: true })),
                },
            },
            _a[Tab.CBI] = {
                legendContent: CBILegendContent,
                factorChart: _jsx(FactorChart, { data: factorChartData }),
                bubbles: {
                    subHeader: _jsx(BubbleSubHeader, { color: BUBBLE_SUBHEADER_COLOR }),
                    title: "Agility & Potential vs. Leadership Capabilities Leader Distribution",
                    chart: (_jsx(CohortInsightsBubbles, { category: Tab.CBI, reportId: reportId || "", colorDescriptions: Colors[Tab.CBI], bubbleScoreRounder: function (value) { return (value === null || value === void 0 ? void 0 : value.toFixed(1)) || ""; }, keyedLabels: cohortSet[Tab.CBI]["Potential"].bubbleChartFields })),
                },
            },
            _a[Tab.ImmersiveMeta] = {
                legendContent: ImmersiveMetaLegendContent,
                factorChart: _jsx(FactorChart, { data: factorChartData }),
                bubbles: {
                    subHeader: _jsx(BubbleSubHeader, { color: BUBBLE_SUBHEADER_COLOR }),
                    title: "Agility & Potential vs. Leadership Capabilities Leader Distribution",
                    chart: (_jsx(CohortInsightsBubbles, { category: Tab.ImmersiveMeta, reportId: reportId || "", colorDescriptions: Colors[Tab.ImmersiveMeta], bubbleScoreRounder: function (value) { return (value === null || value === void 0 ? void 0 : value.toFixed(1)) || ""; }, keyedLabels: cohortSet[Tab.ImmersiveMeta]["Potential"].bubbleChartFields, showAxisBarBorders: true })),
                },
            },
            _a[Tab.HoganHDS] = {
                legendContent: hoganHDSLegendContent,
                factorChart: _jsx(HoganFactorChart, { data: hoganHDSFactorChart }),
            },
            _a[Tab.HoganHPI] = {
                legendContent: hoganHPILegendContent,
                factorChart: _jsx(HoganFactorChart, { data: hoganHPIFactorChart }),
            },
            _a[Tab.HoganMVPI] = {
                legendContent: hoganMVPILegendContent,
                factorChart: _jsx(HoganFactorChart, { data: hoganMVPIFactorChart }),
            },
            _a);
        var config = contentConfig[assessmentCategory] || contentConfig[Tab.CBI];
        return _jsx(TabContent, __assign({}, config));
    };
    var content = renderContent();
    return (_jsx(FilteredOverviewPage, __assign({ backToButton: _jsx(Breadcrumb, {}), secondaryHeadingTitle: reportName, availableFilters: CohortFilterSet, disableFilters: disableFilters, leftTrailer: _jsx("div", __assign({ className: styles.leaderCountContainer }, { children: _jsx(LeaderCount, { leadersCount: ((_f = leaderCountData === null || leaderCountData === void 0 ? void 0 : leaderCountData.data) === null || _f === void 0 ? void 0 : _f.leadersCount) || 0, countText: "leader" }) })), rightTrailer: _jsx(MutateReportButtons, { reportId: reportId || "" }) }, { children: reportId && _jsx(_Fragment, { children: content }) })));
};
